import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import {
    Megaphone,
    Globe,
    ArrowsClockwise,
    MapPin,
    DownloadSimple,
    Sparkle,
    CircleNotch,
    Check,
    DeviceMobile,
    Desktop,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
    buildAdsCsv,
    buildRelatedCsv,
    downloadCsv,
    adsFilename,
    relatedFilename,
    copyToClipboard,
} from "@/lib/csv";
import { scrapeUrl, errText } from "@/lib/api";
import { CopyButton } from "@/components/CopyButton";

const fadeUp = {
    hidden: { opacity: 0, y: 12 },
    show: (i) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.05, duration: 0.3, ease: "easeOut" },
    }),
};

const Section = ({ icon, title, count, action, children }) => (
    <section className="flex flex-col">
        <div className="flex items-center justify-between gap-3 mb-5 pb-3 border-b-2 border-ink">
            <div className="flex items-center gap-2 min-w-0">
                {icon}
                <h2 className="font-heading text-lg font-black uppercase tracking-tight truncate">
                    {title}
                </h2>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                <span className="mono-label">{count} results</span>
                {action}
            </div>
        </div>
        {children}
    </section>
);

export const SerpPanel = ({ ads, organic, related, resolvedLocation, result, onScrapedPage }) => {
    const [scrapingIdx, setScrapingIdx] = useState(null);
    const [scrapedByIdx, setScrapedByIdx] = useState({}); // idx -> CompetitorPage

    // Reset per-organic scrape state whenever the loaded record changes so
    // an earlier record's scrapes don't leak onto a new record's list at the same index.
    useEffect(() => {
        setScrapedByIdx({});
        setScrapingIdx(null);
    }, [result?.id]);

    const handleScrapeOrganic = async (o, i) => {
        if (!o?.link) {
            toast.error("Missing URL");
            return;
        }
        setScrapingIdx(i);
        try {
            const page = await scrapeUrl(o.link, result?.business_type || "", result?.city || "");
            setScrapedByIdx((prev) => ({ ...prev, [i]: page }));
            if (page.error) {
                toast.error(`Scrape failed: ${page.error}`);
            } else {
                toast.success(`Extracted ${page.keywords?.length || 0} keywords from ${o.displayed_link || o.link}`);
                onScrapedPage?.(page);
            }
        } catch (e) {
            toast.error(errText(e, "Scrape failed"));
        } finally {
            setScrapingIdx(null);
        }
    };

    const handleExportAds = () => {
        try {
            downloadCsv(adsFilename(result), buildAdsCsv(result));
            toast.success("Competitor ads CSV downloaded");
        } catch (e) {
            toast.error("Failed to build ads CSV");
        }
    };

    const handleExportRelated = () => {
        try {
            downloadCsv(relatedFilename(result), buildRelatedCsv(result));
            toast.success("Related searches CSV downloaded");
        } catch (e) {
            toast.error("Failed to build related searches CSV");
        }
    };

    const handleCopyAllRelated = async () => {
        const text = (related || []).join("\n");
        const ok = await copyToClipboard(text);
        if (ok) toast.success(`Copied ${related.length} related searches`);
        else toast.error("Copy failed");
    };

    return (
        <div className="p-6 lg:p-10 flex flex-col gap-12 bg-white">
            {resolvedLocation && (
                <div className="flex items-center gap-2 flex-wrap self-start">
                    <div
                        data-testid="resolved-location"
                        className="flex items-center gap-2 border border-ink bg-white px-3 py-2"
                    >
                        <MapPin size={13} weight="bold" className="text-klein" />
                        <span className="mono-label" style={{ color: "#0A0A0A" }}>
                            Google location · {resolvedLocation}
                        </span>
                    </div>
                    <div
                        data-testid="device-indicator"
                        className="flex items-center gap-2 border border-klein bg-klein/5 px-3 py-2"
                    >
                        {(result?.device || "mobile") === "desktop" ? (
                            <Desktop size={13} weight="bold" className="text-klein" />
                        ) : (
                            <DeviceMobile size={13} weight="bold" className="text-klein" />
                        )}
                        <span className="mono-label text-klein">
                            Displaying live {(result?.device || "mobile") === "desktop" ? "Desktop" : "Mobile"} Search results.
                        </span>
                    </div>
                </div>
            )}
            {/* ADS */}
            <Section
                icon={<Megaphone size={18} weight="bold" />}
                title="Competitor Google Ads"
                count={ads.length}
                action={
                    ads.length > 0 && (
                        <button
                            data-testid="export-ads-csv"
                            onClick={handleExportAds}
                            title="Download all competitor ads as CSV"
                            className="brutal-btn border border-ink bg-white text-ink font-mono uppercase tracking-widest text-[10px] font-bold px-2.5 h-7 flex items-center gap-1"
                        >
                            <DownloadSimple size={12} weight="bold" />
                            Ads CSV
                        </button>
                    )
                }
            >
                {ads.length === 0 ? (
                    <div className="text-sm text-neutral-500 leading-relaxed" data-testid="ads-empty">
                        <p>
                            No ads found — we checked both the live SERP <b>and</b> Google's Ad
                            Transparency Center for the top competitor domains and none had
                            active creatives in the last 120 days for this market.
                        </p>
                        <p className="mt-2 text-neutral-600">
                            Try a broader keyword (drop "near me" / use the service noun alone) or
                            a larger city.
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-col">
                        {ads.map((a, i) => (
                            <motion.div
                                key={i}
                                custom={i}
                                initial="hidden"
                                animate="show"
                                variants={fadeUp}
                                data-testid={`ad-item-${i}`}
                                className="py-5 border-b border-neutral-200 last:border-b-0"
                            >
                                <div className="flex flex-wrap items-center gap-2 mb-1.5">
                                    <span className="inline-block border border-klein px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-klein">
                                        Sponsored
                                    </span>
                                    {a.source === "transparency" && (
                                        <span
                                            className="inline-block border border-ink bg-ink px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest text-white"
                                            title="Sourced from Google Ad Transparency Center"
                                            data-testid={`ad-source-transparency-${i}`}
                                        >
                                            Ad Library
                                        </span>
                                    )}
                                    <span className="mono-label truncate">
                                        {a.displayed_link || a.link}
                                    </span>
                                </div>
                                <div className="flex gap-4">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-heading text-base font-bold text-ink leading-snug">
                                            {a.title || "(no headline)"}
                                        </h3>
                                        <p className="text-sm text-neutral-700 mt-1.5 leading-relaxed">
                                            {a.description || "(no description)"}
                                        </p>
                                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                                            {a.last_shown && (
                                                <p className="mono-label">
                                                    last shown: {a.last_shown.slice(0, 10)}
                                                </p>
                                            )}
                                            <CopyButton
                                                text={`${a.title || ""}\n${a.description || ""}\n${a.displayed_link || ""}`}
                                                label="Copy ad"
                                                variant="solid"
                                                dataTestId={`ad-copy-${i}`}
                                            />
                                        </div>
                                    </div>
                                    {a.preview_image && (
                                        <a
                                            href={a.link || a.preview_image}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="shrink-0 border border-neutral-300 hover:border-ink transition-colors duration-150"
                                            title="View original ad in Google Ad Transparency Center"
                                            data-testid={`ad-preview-${i}`}
                                        >
                                            <img
                                                src={a.preview_image}
                                                alt="ad screenshot"
                                                className="w-24 h-24 object-cover bg-neutral-100"
                                                loading="lazy"
                                            />
                                        </a>
                                    )}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </Section>

            {/* ORGANIC */}
            <Section
                icon={<Globe size={18} weight="bold" />}
                title="Top 5 Organic Results"
                count={organic.length}
            >
                {organic.length === 0 ? (
                    <p className="text-sm text-neutral-500" data-testid="organic-empty">
                        No organic results returned.
                    </p>
                ) : (
                    <div className="flex flex-col">
                        {organic.map((o, i) => {
                            const scraped = scrapedByIdx[i];
                            const isScraping = scrapingIdx === i;
                            return (
                            <motion.div
                                key={i}
                                custom={i}
                                initial="hidden"
                                animate="show"
                                variants={fadeUp}
                                data-testid={`organic-item-${i}`}
                                className="py-5 border-b border-neutral-200 last:border-b-0"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <span className="mono-label block truncate">
                                            {o.displayed_link || o.link}
                                        </span>
                                        <a
                                            href={o.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-heading text-base font-bold text-klein hover:underline leading-snug mt-1 inline-block"
                                        >
                                            {o.title || o.link}
                                        </a>
                                        <p className="text-sm text-neutral-700 mt-1.5 leading-relaxed">
                                            {o.snippet || "(no snippet)"}
                                        </p>
                                    </div>
                                    <button
                                        data-testid={`organic-scrape-${i}`}
                                        onClick={() => handleScrapeOrganic(o, i)}
                                        disabled={isScraping || !o.link}
                                        title={scraped ? "Re-scrape this page" : "Extract keywords + positioning from this page"}
                                        className="brutal-btn shrink-0 border border-ink bg-white text-ink font-mono uppercase tracking-widest text-[10px] font-bold px-2.5 h-7 flex items-center gap-1 disabled:opacity-50"
                                    >
                                        {isScraping ? (
                                            <CircleNotch size={11} weight="bold" className="animate-spin" />
                                        ) : scraped && !scraped.error ? (
                                            <Check size={11} weight="bold" />
                                        ) : (
                                            <Sparkle size={11} weight="bold" />
                                        )}
                                        {isScraping ? "Scraping" : scraped && !scraped.error ? "Rescan" : "Scrape Keywords"}
                                    </button>
                                </div>

                                {scraped && !scraped.error && (
                                    <div
                                        data-testid={`organic-scrape-result-${i}`}
                                        className="mt-3 border border-ink bg-neutral-50 p-3 space-y-2"
                                    >
                                        {scraped.positioning && (
                                            <p className="text-xs text-neutral-700 leading-relaxed">
                                                <span className="mono-label mr-1.5">Positioning:</span>
                                                {scraped.positioning}
                                            </p>
                                        )}
                                        {scraped.angles?.length > 0 && (
                                            <div className="flex flex-wrap gap-1">
                                                {scraped.angles.map((a, ai) => (
                                                    <span
                                                        key={ai}
                                                        className="border border-neutral-400 bg-white px-1.5 py-0.5 font-mono text-[10px] text-ink"
                                                    >
                                                        {a}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {scraped.keywords?.length > 0 && (
                                            <div className="flex flex-wrap gap-1">
                                                {scraped.keywords.map((k, ki) => (
                                                    <button
                                                        key={ki}
                                                        onClick={async () => {
                                                            const ok = await copyToClipboard(k);
                                                            if (ok) toast.success(`Copied "${k}"`);
                                                        }}
                                                        className="border border-neutral-300 bg-white hover:border-ink hover:bg-ink hover:text-white transition-colors px-2 py-0.5 font-mono text-[11px] text-ink"
                                                    >
                                                        {k}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        <div className="pt-1">
                                            <button
                                                onClick={async () => {
                                                    const ok = await copyToClipboard((scraped.keywords || []).join("\n"));
                                                    if (ok) toast.success(`Copied ${scraped.keywords?.length || 0} keywords`);
                                                }}
                                                className="mono-label underline decoration-neutral-400 hover:decoration-ink"
                                            >
                                                Copy all keywords
                                            </button>
                                        </div>
                                    </div>
                                )}
                                {scraped?.error && (
                                    <p
                                        data-testid={`organic-scrape-error-${i}`}
                                        className="mt-2 text-xs text-red-600 font-mono"
                                    >
                                        {scraped.error}
                                    </p>
                                )}
                            </motion.div>
                            );
                        })}
                    </div>
                )}
            </Section>

            {/* RELATED */}
            <Section
                icon={<ArrowsClockwise size={18} weight="bold" />}
                title="Related Searches"
                count={related.length}
                action={
                    related.length > 0 && (
                        <>
                            <button
                                data-testid="copy-all-related"
                                onClick={handleCopyAllRelated}
                                title="Copy all related searches"
                                className="brutal-btn border border-ink bg-white text-ink font-mono uppercase tracking-widest text-[10px] font-bold px-2.5 h-7 flex items-center gap-1"
                            >
                                Copy all
                            </button>
                            <button
                                data-testid="export-related-csv"
                                onClick={handleExportRelated}
                                title="Download related searches as CSV"
                                className="brutal-btn border border-ink bg-white text-ink font-mono uppercase tracking-widest text-[10px] font-bold px-2.5 h-7 flex items-center gap-1"
                            >
                                <DownloadSimple size={12} weight="bold" />
                                CSV
                            </button>
                        </>
                    )
                }
            >
                {related.length === 0 ? (
                    <p className="text-sm text-neutral-500" data-testid="related-empty">
                        No related searches surfaced.
                    </p>
                ) : (
                    <div className="flex flex-wrap gap-2" data-testid="related-list">
                        {related.map((r, i) => (
                            <button
                                key={i}
                                data-testid={`related-item-${i}`}
                                title="Click to copy"
                                onClick={async (e) => {
                                    e.preventDefault();
                                    const ok = await copyToClipboard(r);
                                    if (ok) toast.success(`Copied "${r}"`);
                                }}
                                className="border border-neutral-300 bg-white hover:border-ink hover:bg-ink hover:text-white transition-colors duration-150 px-3 py-1.5 font-mono text-xs text-ink cursor-pointer text-left"
                            >
                                {r}
                            </button>
                        ))}
                    </div>
                )}
            </Section>
        </div>
    );
};
